import { useMemo } from 'react';
import { Users } from 'lucide-react';
import type { ActiveUser, ActiveUsersSnapshot, PresenceStatus } from '@/types';
import { useAuth } from '@/store/auth-context';
import { usePlatformEvents } from '@/contexts/PlatformEventContext';
import { OverviewPanel } from './OverviewPanel';

interface ActiveUsersPanelProps {
  data?: ActiveUsersSnapshot;
  isLoading?: boolean;
}

const STATUS_DOT: Record<PresenceStatus, string> = {
  online: 'bg-emerald-500',
  idle: 'bg-amber-500',
  away: 'bg-zinc-400',
};

function displayName(user: ActiveUser): string {
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (full) return full;
  return user.email ?? user.userId;
}

function initials(user: ActiveUser): string {
  const first = (user.firstName ?? '').trim();
  const last = (user.lastName ?? '').trim();
  if (first || last) return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase();
  if (user.email) return user.email[0]?.toUpperCase() ?? '?';
  return '?';
}

function humanizeDuration(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  if (diffMs < 60_000) return 'just now';
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  const hours = Math.floor(diffMs / 3_600_000);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  const days = Math.floor(diffMs / 86_400_000);
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

function secondaryLine(user: ActiveUser): string {
  if (user.status === 'away') {
    return user.disconnectedAt
      ? `disconnected since ${humanizeDuration(user.disconnectedAt)}`
      : 'disconnected';
  }
  if (user.status === 'idle') {
    return user.connectedSince
      ? `idle since ${humanizeDuration(user.connectedSince)}`
      : 'idle';
  }
  return user.connectedSince
    ? `online since ${humanizeDuration(user.connectedSince)}`
    : 'online';
}

export function ActiveUsersPanel({ data, isLoading }: ActiveUsersPanelProps) {
  const { user: currentUser } = useAuth();
  const { isConnected, diagnostics } = usePlatformEvents();

  // The self entry is fully client-driven: status mirrors the WS connection,
  // and the row is always present regardless of what the server reports.
  // Server-provided self entries are filtered out so we never double-render.
  const users = useMemo<ActiveUser[]>(() => {
    const others = (data?.users ?? []).filter(
      (u) => !currentUser || u.userId !== currentUser.userId,
    );
    if (!currentUser) return others;
    const selfEntry: ActiveUser = {
      userId: currentUser.userId,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      email: currentUser.email,
      avatarUrl: currentUser.avatarUrl,
      status: isConnected ? 'online' : 'away',
      connectedSince:
        isConnected && diagnostics.connectedSince
          ? new Date(diagnostics.connectedSince).toISOString()
          : undefined,
      disconnectedAt:
        !isConnected && diagnostics.lastDisconnectTime
          ? new Date(diagnostics.lastDisconnectTime).toISOString()
          : undefined,
    };
    return [selfEntry, ...others];
  }, [data, currentUser, isConnected, diagnostics.connectedSince, diagnostics.lastDisconnectTime]);

  return (
    <OverviewPanel
      title="Active Users"
      icon={<Users className="h-5 w-5" />}
      isLoading={isLoading && !data}
      isEmpty={!isLoading && users.length === 0}
      emptyMessage="No teammates online"
      bodyClassName="p-0 min-h-24"
    >
      <ul className="divide-y divide-border">
        {users.map((user) => {
          const status = user.status ?? 'online';
          const secondary = secondaryLine(user);
          const isAway = status === 'away';
          return (
            <li
              key={user.userId}
              className="flex items-center gap-3 px-4 py-2.5"
            >
              <div className="flex w-10 shrink-0 justify-center">
                <div className="relative">
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt=""
                      className={
                        'h-8 w-8 rounded-full object-cover ' +
                        (isAway ? 'opacity-60' : '')
                      }
                    />
                  ) : (
                    <div
                      className={
                        'flex h-8 w-8 items-center justify-center rounded-full bg-muted text-sm font-semibold text-muted-foreground ' +
                        (isAway ? 'opacity-60' : '')
                      }
                    >
                      {initials(user)}
                    </div>
                  )}
                  <span
                    className={
                      'absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-card ' +
                      STATUS_DOT[status]
                    }
                    aria-label={status}
                  />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className={
                    'text-sm truncate ' +
                    (isAway ? 'text-muted-foreground' : 'text-foreground')
                  }
                >
                  {displayName(user)}
                </p>
                <p className="text-xs text-muted-foreground truncate">{secondary}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </OverviewPanel>
  );
}
